import { View, html, on, CustomEventWithDetail, UnsafeHtml, rune } from 'rune-ts';
import klass from './LandingDropdown.module.scss';
import { typoPcMo } from '../../../../shared/typography/typo';
import { htmlIf } from '../../../../shared/util';
import { DropdownDownIcon, DropdownUpIcon } from '../../atoms/Icon';

type OptionViewProps = {
  label: string;
  value: string;
  selected?: boolean;
};
type DropdownViewProps = {
  options: OptionViewProps[];
  place_holder: string | UnsafeHtml;
  required?: boolean;
  name: string;
};

export class DropdownOptionSelectedEvent extends CustomEventWithDetail<OptionViewProps> {}

export class OptionView extends View<OptionViewProps> {
  @on('click')
  onSelect() {
    this.data.selected = true;

    this.dispatchEvent(DropdownOptionSelectedEvent, { bubbles: true, detail: this.data });
  }

  override template({ label, selected }: OptionViewProps) {
    const is_mobile = !!rune.getSharedData(this)?.is_mobile;

    return html`
      <div
        class="${klass.option} ${selected ? klass.selected : ''} ${typoPcMo({
          is_mobile,
          pc: '14_medium',
          mo: '14_medium',
        })}"
      >
        ${label}
      </div>
    `;
  }
}

export class LandingDropdown extends View<DropdownViewProps> {
  private _isOpen = false;
  private _selectedOption?: OptionViewProps;

  onToggleDropdown() {
    this._isOpen = !this._isOpen;
  }

  @on(DropdownOptionSelectedEvent)
  onOptionClick(event: CustomEvent<OptionViewProps>) {
    this.data.options.forEach((option) => (option.selected = false));
    event.detail.selected = true;
    this.onToggleDropdown();
    this.redraw();
    const input_element = this.element().querySelector('input');
    if (input_element) {
      input_element.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  @on('click', `.${klass.dropdown_header}`)
  onOptionHeaderClick() {
    this.onToggleDropdown();
    this.redraw();
  }

  override template({ name, options, place_holder, required }: DropdownViewProps) {
    const is_mobile = !!rune.getSharedData(this)?.is_mobile;
    const is_active = !!this._selectedOption && !!this._selectedOption.value;

    return html`
      <div class="${klass.dropdown}" data-active="${is_active}">
        <label class="${klass.dropdown_header} ${typoPcMo({ is_mobile, pc: '16_medium', mo: '14_medium' })}">
          <input
            name="${name}"
            type="text"
            ${this._selectedOption ? html`value="${this._selectedOption.value}"` : ''}
            ${required ? html`required=""` : ''}
          />
          <span class="${klass.text_label}">
            ${this._selectedOption ? this._selectedOption.label : place_holder}
            ${htmlIf(html`<span class="${klass.asterisk}"> *</span>`, !!required)}
          </span>
          <span class="${klass.icon_arrow}">${this._isOpen ? DropdownUpIcon : DropdownDownIcon}</span>
        </label>
        ${this._isOpen
          ? html`
              <div class="${klass.dropdown_options}">${options.map((option) => new OptionView(option))}</div>
            `
          : ''}
      </div>
    `;
  }

  override redraw(): this {
    this._selectedOption = this.data.options.find((option) => option.selected);
    return super.redraw();
  }

  getSelectedValue() {
    return this._selectedOption ? this._selectedOption.value : null;
  }
}
